import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, Subject, firstValueFrom, forkJoin, map, mergeMap, of, switchMap } from 'rxjs';
import { ICoordate } from '@agenda-customer-ui/shared';
import { HeremapsService } from '../heremaps/heremaps.service';
import { formatHourDefault } from '@core/utils';
import { ICategoryResponse, IDealerByLocation, IDefaultService, IResponseModel } from '@core/interface';
import { API, environment } from '@core/envs';
import { GetCountryService } from '@core/services';

@Injectable({
  providedIn: 'root'
})
export class DealerService {
  private readonly http = inject(HttpClient);
  private readonly heremapsService = inject(HeremapsService);
  readonly #getCountryService = inject(GetCountryService);

  dealer!: IDealerByLocation;
  private favoriteDealerSource = new Subject<IDealerByLocation>();
  favoriteDealer$ = this.favoriteDealerSource.asObservable();

  getCategoriesByDealerId(): Observable<ICategoryResponse[]> {
    const { countryCode, dealerId } = this.dealer;
    const api = environment.getRoute(API.dealer);
    const url = `${api}/catalog/categories?countryCode=${countryCode}&dealerCode=${dealerId}`;

    return this.http.get<ICategoryResponse[]>(url).pipe(
      mergeMap((categories) => {
        const servicesObservables = categories.map((category) => {
          if (!category.recall) {
            return this.getServicesByDealerId(category.id).pipe(
              map((services) => {
                category.availableServices = services;
                return category;
              })
            );
          } else {
            return of(category);
          }
        });
        return forkJoin(servicesObservables);
      })
    );
  }

  getServicesByDealerId(categoryId: number): Observable<IDefaultService[]> {
    const { countryCode, dealerId } = this.dealer;
    const api = environment.getRoute(API.dealer);
    const url = `${api}/catalog/category/${categoryId}/services?dealerCode=${dealerId}&countryCode=${countryCode}`;

    return this.http.get<IDefaultService[]>(url);
  }

  getListAdvisors(dealerId: string, country: string): Observable<IResponseModel<any>> {
    const api = environment.getRoute(API.dealer);
    const url = `${api}/dealers/${dealerId}/advisors?country=${country}`;

    return this.http.get<IResponseModel<any>>(url);
  }

  getDealerByGeocode(
    latUser: number,
    longUser: number,
    latSearch: number,
    longSearch: number,
    pickup: boolean,
    modelGroup: string | null,
    countryCode: string
  ): Observable<Array<IDealerByLocation>> {
    const api = environment.getRoute(API.dealer);
    const url = `${api}/dealers/coordinates`;

    const body = {
      currentLat: latUser,
      currentLng: longUser,
      lat: latSearch,
      lng: longSearch,
      modelGroup: modelGroup ?? '',
      pickUp: pickup,
      countryCode
    };

    const origin: ICoordate = { lat: latSearch, lng: longSearch };

    return this.http.post<IResponseModel<Array<IDealerByLocation>>>(url, body).pipe(
      switchMap(async (response) => {
        const dealerList = response?.response || [];

        const distanceObservables = dealerList.map(async (dealer) => {
          dealer.distance = Number((dealer.distance / 1000).toFixed(1)); // Convertendo de metros para quilômetros;
          dealer.selected = false;

          dealerList.sort((a, b) => a.distance - b.distance);
          this.formatHour(dealer);
        });

        await Promise.all(distanceObservables);
        return dealerList;
      })
    );
  }

  async getDealerByIdCode(idCode: number, coordinate?: ICoordate, modelGroup?: string): Promise<IDealerByLocation> {
    const api = environment.getRoute(API.dealer);
    const url = `${api}/dealers/${idCode}?country=${this.#getCountryService.getCountry()}&modelGroup=${modelGroup ?? ''}`;

    try {
      const data = await firstValueFrom(this.http.get<IResponseModel<IDealerByLocation>>(url));

      if (data && data['response']) {
        const dealer: IDealerByLocation = data['response'];
        dealer.selected = false;
        if (dealer) {
          const destination: ICoordate = {
            lat: dealer.latitude,
            lng: dealer.longitude
          };

          if (coordinate) {
            const distance = await this.heremapsService.getDistance(coordinate, destination);
            dealer.distance = distance;
          }

          this.formatHour(dealer);
        }

        return dealer;
      }

      throw new Error('Response or response.response is undefined.');
    } catch (error) {
      console.error("Error fetching dealer data:", error);
      throw error;
    }
  }

  private formatHour(dealer: IDealerByLocation): void {
    dealer.highestTimeWeek = '';
    dealer.lowestTimeWeek = '';
    dealer.highestTimeWeekend = '';
    dealer.lowestTimeWeekend = '';
    dealer.lowestDay = 0;
    dealer.openSaturday = false;
    dealer.openSunday = false;

    if (dealer) {
      if (dealer.serviceHours !== null && dealer.serviceHours) {
        for (let index = 0; index <= dealer.serviceHours.length; index++) {
          if (dealer.serviceHours[index]) {
            if (
              dealer.serviceHours[index].open &&
              dealer.serviceHours[index].open !== '-' &&
              dealer.serviceHours[index].close !== '-'
            ) {
              if (dealer.serviceHours[index].days[0] <= 5) {
                // define horario de abertura na semana
                if (dealer.lowestTimeWeek === '') {
                  dealer.lowestTimeWeek = formatHourDefault(
                    dealer.serviceHours[index].open
                  );
                  dealer.lowestDay = index;
                }
                // define horario de fechamento semana
                if (dealer.serviceHours[index].days[0] >= dealer.lowestDay) {
                  dealer.highestTimeWeek = formatHourDefault(
                    dealer.serviceHours[index].close
                  );
                }
              } else {
                // regra fim de semana
                if (dealer.serviceHours[index].days[0] === 6) {
                  dealer.openSaturday = true;
                  dealer.lowestTimeWeekend = formatHourDefault(
                    dealer.serviceHours[index].open
                  );
                  dealer.highestTimeWeekend = formatHourDefault(
                    dealer.serviceHours[index].close
                  );
                }
                if (dealer.serviceHours[index].days[0] === 7) {
                  dealer.openSunday = true;
                  if (dealer.lowestTimeWeekend === '') {
                    dealer.lowestTimeWeekend = formatHourDefault(
                      dealer.serviceHours[index].open
                    );
                  }
                  dealer.highestTimeWeekend = formatHourDefault(
                    dealer.serviceHours[index].close
                  );
                }
              }
            }
          }
        }
      }
    }
  }

  newPreferredDealer(dealer: IDealerByLocation) {
    this.favoriteDealerSource.next(dealer);
  }

}
